import React, { useEffect } from "react";
import PropTypes from "prop-types";
import { useForm, Controller } from "react-hook-form";
import {
    useCreateTierMutation,
    useUpdateTierMutation,
} from "../../../redux/service/modules/admin";
import { useNotification } from "../../../context/NotificationContext";
import { FormField } from "../../../components/common/FormField";
import { ActionButtons } from "../../../components/common/ActionButton";
import CommonDialog from "../../../components/common/CommonDialog";

const TierDialog = ({ open, onClose, rowData, isEdit, title, ctaText }) => {
    const { control, handleSubmit, setValue, reset, setError } = useForm();
    const [createTier, { isLoading: isCreating }] = useCreateTierMutation();
    const [updateTier, { isLoading: isUpdating }] = useUpdateTierMutation();
    const { showNotification } = useNotification();

    useEffect(() => {
        if (rowData) {
            Object.keys(rowData).forEach((key) => {
                setValue(key, rowData[key]);
            });
        }
    }, [rowData, setValue]);

    const handleClose = () => {
        reset();
        onClose();
    };

    const onSubmit = async (data) => {
        try {
            const formDataToSend = new FormData();
            Object.keys(data).forEach((key) => {
                formDataToSend.append(key, data[key]);
            });
            if (isEdit) {
                await updateTier({ data: formDataToSend })
                    .unwrap()
                    .then((response) => {
                        if (response) {
                            showNotification(
                                "success",
                                "Tier Updated successfully!"
                            );
                            handleClose();
                        } else {
                            showNotification(
                                "error",
                                "An Error Occurred",
                                "Failed to Update Tier"
                            );
                        }
                    })
                    .catch((error) => {
                        if (error.status === 400) {
                            setError("tier_name", {
                                type: "manual",
                                message: error.data.error,
                            });
                        }
                    });
            } else {
                await createTier({ data: formDataToSend })
                    .unwrap()
                    .then((response) => {
                        if (response) {
                            showNotification(
                                "success",
                                "Tier Created successfully!"
                            );
                            handleClose();
                        } else {
                            showNotification(
                                "error",
                                "An Error Occurred",
                                "Failed to Create Tier"
                            );
                            handleClose();
                        }
                    })
                    .catch((error) => {
                        if (error.status === 400) {
                            setError("tier_name", {
                                type: "manual",
                                message: error.data.error,
                            });
                        } else {
                            showNotification(
                                "error",
                                "An Error Occurred",
                                "Failed to Create Tier"
                            );
                        }
                    });
            }
        } catch (error) {
            console.error("Failed to update tier:", error);
        }
    };

    return (
        <CommonDialog open={open} onClose={handleClose} title={title}>
            <Controller
                name="tier_name"
                control={control}
                rules={{
                    required: "Tier Name is required",
                }}
                render={({ field, fieldState }) => (
                    <div className="space-y-1">
                        <FormField label="Tier Name" {...field} required />
                        {fieldState.error && (
                            <span className="xl:text-xs 2xl:text-sm 3xl:text-base text-red-500">
                                {fieldState.error.message}
                            </span>
                        )}
                    </div>
                )}
            />

            <Controller
                name="weightage"
                control={control}
                rules={{
                    required: "Weightage is required",
                    validate: (value) =>
                        !isNaN(value) || "Weightage must be a number",
                }}
                render={({ field, fieldState }) => (
                    <div className="space-y-1">
                        <FormField
                            label="Weightage"
                            type="number"
                            {...field}
                            required
                        />
                        {fieldState.error && (
                            <span className="xl:text-xs 2xl:text-sm 3xl:text-base text-red-500">
                                {fieldState.error.message}
                            </span>
                        )}
                    </div>
                )}
            />

            <Controller
                name="tier_priority"
                control={control}
                rules={{
                    required: "Tier Priority is required",
                    validate: (value) =>
                        !isNaN(value) || "Tier Priority must be a number",
                }}
                render={({ field, fieldState }) => (
                    <div className="space-y-1">
                        <FormField
                            label="Tier Priority"
                            type="number"
                            {...field}
                            required
                        />
                        {fieldState.error && (
                            <span className="xl:text-xs 2xl:text-sm 3xl:text-base text-red-500">
                                {fieldState.error.message}
                            </span>
                        )}
                    </div>
                )}
            />

            <ActionButtons
                onCancel={handleClose}
                ctaText={ctaText}
                onSave={handleSubmit(onSubmit)}
                isLoading={isCreating || isUpdating}
            />
        </CommonDialog>
    );
};

TierDialog.propTypes = {
    open: PropTypes.bool.isRequired,
    onClose: PropTypes.func.isRequired,
    rowData: PropTypes.object,
    isEdit: PropTypes.bool,
    title: PropTypes.string,
    ctaText: PropTypes.string,
};

export default TierDialog;
